/**
 * spin-viewer
 * 360 drag-to-spin frame sequence viewer with auto-rotate, wheel/pinch zoom,
 * and pan-while-zoomed.
 *
 * Expects:
 *   <spin-viewer data-autoplay-speed="120">
 *     <script type="application/json" data-spin-frames>["url", ...]</script>
 *     <img data-spin-image src="...">
 *     <span data-spin-progress></span>
 *   </spin-viewer>
 *
 * Frames are fetched lazily: if the element sits inside a <modal-dialog>,
 * loading is deferred until that dialog gains the [open] attribute.
 * Otherwise it loads on first intersection.
 *
 * Auto-rotate begins once all frames are cached and the viewer is visible.
 * It stops permanently the moment the user touches the viewer.
 *
 * Interaction model:
 *   - At scale 1x, drag spins the sequence.
 *   - Wheel (desktop) or two-finger pinch (mobile) zooms, anchored on the
 *     cursor / pinch midpoint.
 *   - Above 1x, drag pans the zoomed frame (spin is suspended).
 *   - Double-click, or zooming back to 1x, restores spin.
 *
 * Attributes:
 *   data-autoplay-speed  ms per frame. Default 120. 0 disables autoplay.
 */
class SpinViewer extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-spin-frames]');
    this.img = this.querySelector('[data-spin-image]');
    if (!json || !this.img) return;

    try {
      this.frames = JSON.parse(json.textContent);
    } catch (e) {
      console.error('[spin-viewer] bad frame JSON', e);
      return;
    }
    if (!Array.isArray(this.frames) || this.frames.length < 2) return;

    this.reportGaps();

    this.progress = this.querySelector('[data-spin-progress]');
    this.index = 0;
    this.settled = 0;
    this.cache = new Array(this.frames.length);
    this.dragging = false;
    this.startX = 0;
    this.startY = 0;
    this.startIndex = 0;
    this.booted = false;

    // Auto-rotate state
    this.speed = parseInt(this.dataset.autoplaySpeed, 10);
    if (isNaN(this.speed)) this.speed = 120;
    this.rafId = null;
    this.lastStep = 0;
    this.userEngaged = false;
    this.reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Zoom / pan state
    this.pointers = new Map();
    this.scale = 1;
    this.minScale = 1;
    this.maxScale = 4;
    this.panX = 0;
    this.panY = 0;
    this.pinchBase = null;
    this.startPanX = 0;
    this.startPanY = 0;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.loop = this.loop.bind(this);

    this.addEventListener('pointerdown', this.onDown);
    this.addEventListener('pointermove', this.onMove);
    this.addEventListener('pointerup', this.onUp);
    this.addEventListener('pointercancel', this.onUp);
    this.addEventListener('dragstart', (e) => e.preventDefault());

    // Any deliberate contact kills autoplay for good, even a tap that
    // never becomes a drag.
    this.addEventListener('pointerdown', () => this.stopAuto(true), { once: true });

    // Wheel zooms (and stops autoplay). passive:false so preventDefault works.
    this.addEventListener('wheel', this.onWheel, { passive: false });
    this.addEventListener('dblclick', () => this.resetZoom());

    this.modal = this.closest('modal-dialog');

    if (this.modal) {
      if (this.isVisible) this.preload();
      this.observer = new MutationObserver(() => {
        if (this.isVisible) {
          this.preload();
          this.startAuto();
        } else {
          this.stopAuto(false); // pause only; reopening may resume
          this.resetZoom();     // don't carry zoom state into a reopen
        }
      });
      this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    } else {
      this.observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries[0].isIntersecting) {
            obs.disconnect();
            this.preload();
            this.startAuto();
          }
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.stopAuto(false);
    if (this.observer) this.observer.disconnect();
  }

  /** True when the viewer is actually on screen (modal open, or no modal at all). */
  get isVisible() {
    return !this.modal || this.modal.hasAttribute('open');
  }

  /**
   * Console-only diagnostic. Parses the zero-padded frame number out of each
   * filename and reports any missing numbers in the sequence.
   */
  reportGaps() {
    const nums = this.frames
      .map((u) => (u.match(/_(\d{4,5})_/) || [])[1])
      .filter(Boolean)
      .map(Number);
    if (nums.length < 2) return;

    const seen = new Set(nums);
    const gaps = [];
    for (let i = Math.min(...nums); i <= Math.max(...nums); i++) {
      if (!seen.has(i)) gaps.push(i);
    }
    if (gaps.length) {
      console.warn(
        `[spin-viewer] ${this.frames.length} frames loaded. Missing from sequence: ${gaps.join(', ')}`
      );
    }
  }

  /**
   * Concurrency-limited loader. Firing all N requests at once saturates the
   * connection and delays the early frames the user actually sees first, so
   * work through the sequence in order with a small window in flight.
   */
  preload() {
    if (this.booted) return;
    this.booted = true;
    this.setAttribute('loading', '');

    const CONCURRENCY = 6;
    let next = 0;

    const startOne = () => {
      if (next >= this.frames.length) return;
      const i = next++;
      const im = new Image();
      im.decoding = 'async';
      im.onload = () => {
        this.cache[i] = im;
        this.tick();
        startOne();
      };
      im.onerror = () => {
        this.cache[i] = null; // never cache a broken image
        this.tick();
        startOne();
      };
      im.src = this.frames[i];
    };

    for (let k = 0; k < Math.min(CONCURRENCY, this.frames.length); k++) startOne();
  }

  tick() {
    this.settled++;
    if (this.progress) {
      this.progress.style.setProperty('--p', this.settled / this.frames.length);
    }
    if (this.settled === this.frames.length) {
      this.removeAttribute('loading');
      if (this.cache.some(Boolean)) {
        this.setAttribute('ready', '');
        this.startAuto();
      }
    }
  }

  /* ---- auto-rotate ------------------------------------- */

  startAuto() {
    if (this.rafId !== null) return;
    if (this.userEngaged || this.reducedMotion) return;
    if (this.speed <= 0) return;
    if (!this.hasAttribute('ready')) return; // tick() will call again once ready
    if (!this.isVisible) return;

    this.setAttribute('spinning', '');
    this.lastStep = 0;
    this.rafId = requestAnimationFrame(this.loop);
  }

  /**
   * @param {boolean} permanent - true when the user has taken control; autoplay
   *                              will never resume for this element.
   */
  stopAuto(permanent) {
    if (permanent) this.userEngaged = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.removeAttribute('spinning');
  }

  loop(now) {
    if (this.userEngaged || !this.isVisible) {
      this.stopAuto(false);
      return;
    }

    if (!this.lastStep) this.lastStep = now;
    const elapsed = now - this.lastStep;

    if (elapsed >= this.speed) {
      // Advance by however many frames the elapsed time covers, so a janky
      // frame doesn't slow the rotation down.
      const steps = Math.floor(elapsed / this.speed);
      const n = this.frames.length;
      this.index = (this.index + steps) % n;
      this.render();
      this.lastStep = now - (elapsed % this.speed);
    }

    this.rafId = requestAnimationFrame(this.loop);
  }

  /* ---- rendering --------------------------------------- */

  /** Nearest successfully-loaded frame to index i, searching both directions. */
  nearest(i) {
    const n = this.frames.length;
    for (let d = 1; d < n; d++) {
      const a = this.cache[(i + d) % n];
      if (a) return a;
      const b = this.cache[(((i - d) % n) + n) % n];
      if (b) return b;
    }
    return null;
  }

  render() {
    // Only swaps the source; never touches transform, so zoom/pan survive
    // frame changes.
    const f = this.cache[this.index] || this.nearest(this.index);
    if (f) this.img.src = f.src;
  }

  /* ---- drag / spin / pan ------------------------------- */

  onDown(e) {
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.setPointerCapture(e.pointerId);

    if (this.pointers.size === 2) {
      this.startPinch();
      this.dragging = false; // second finger cancels any spin/pan drag
      return;
    }

    if (!this.hasAttribute('ready')) return;
    this.dragging = true;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startIndex = this.index;
    this.startPanX = this.panX;
    this.startPanY = this.panY;
  }

  onMove(e) {
    if (this.pointers.has(e.pointerId)) {
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (this.pointers.size === 2) {
      e.preventDefault();
      this.movePinch();
      return;
    }

    if (!this.dragging) return;
    e.preventDefault();

    if (this.scale > 1) {
      // Pan the zoomed frame.
      this.panX = this.startPanX + (e.clientX - this.startX);
      this.panY = this.startPanY + (e.clientY - this.startY);
      this.clampPan();
      this.applyTransform();
    } else {
      // Spin.
      const n = this.frames.length;
      const step = this.offsetWidth / n; // full-width drag == one full 360
      const shift = Math.round((e.clientX - this.startX) / step);
      const next = (((this.startIndex + shift) % n) + n) % n;
      if (next === this.index) return;
      this.index = next;
      this.render();
    }
  }

  onUp(e) {
    this.pointers.delete(e.pointerId);
    if (this.hasPointerCapture && this.hasPointerCapture(e.pointerId)) {
      this.releasePointerCapture(e.pointerId);
    }
    if (this.pointers.size < 2) this.pinchBase = null;
    if (this.pointers.size === 0) this.dragging = false;
  }

  /* ---- zoom -------------------------------------------- */

  onWheel(e) {
    e.preventDefault();
    this.stopAuto(true);
    const factor = Math.exp(-e.deltaY * 0.0015);
    this.zoomAt(this.scale * factor, e.clientX, e.clientY);
  }

  startPinch() {
    const [a, b] = [...this.pointers.values()];
    this.pinchBase = {
      dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      scale: this.scale,
    };
    this.stopAuto(true);
  }

  movePinch() {
    if (!this.pinchBase) return this.startPinch();
    const [a, b] = [...this.pointers.values()];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    this.zoomAt(this.pinchBase.scale * (dist / this.pinchBase.dist), mx, my);
  }

  /** Zoom to `target` scale, anchored so the point (px,py) stays put. */
  zoomAt(target, px, py) {
    const s = Math.min(this.maxScale, Math.max(this.minScale, target));
    const rect = this.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const lx = (px - cx - this.panX) / this.scale;
    const ly = (py - cy - this.panY) / this.scale;
    this.scale = s;
    this.panX = px - cx - lx * s;
    this.panY = py - cy - ly * s;
    if (s <= 1) {
      this.panX = 0;
      this.panY = 0;
    }
    this.clampPan();
    this.applyTransform();
    this.toggleAttribute('zoomed', s > 1);
  }

  /** Keep the scaled image from being dragged completely out of view. */
  clampPan() {
    const rect = this.getBoundingClientRect();
    const mx = ((this.scale - 1) * rect.width) / 2;
    const my = ((this.scale - 1) * rect.height) / 2;
    this.panX = Math.max(-mx, Math.min(mx, this.panX));
    this.panY = Math.max(-my, Math.min(my, this.panY));
  }

  applyTransform() {
    this.img.style.transform =
      `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
  }

  resetZoom() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    this.removeAttribute('zoomed');
    this.applyTransform();
  }
}

if (!customElements.get('spin-viewer')) {
  customElements.define('spin-viewer', SpinViewer);
}
